import { HeatingOrCoolingSource } from '../../../generated/graphql'
import { reduceToSum } from '../../../util/array-utility'

export enum HeatSourceType {
  Ground = 'ground',
  Air = 'air',
  EnergyBus = 'energyBus',
  Waste = 'waste',
}

export type HeatSource = {
  type: HeatSourceType
  temperature: number
  maxPower: number
  collectedHeat?: number
}

function getHeatSourceTemperature(
  source: HeatingOrCoolingSource,
  groundHeatSourceTemperature: number | undefined,
  airHeatSourceMinTemperature: number | undefined,
  outdoorTemperature: number,
): number | null {
  switch (source) {
    case HeatingOrCoolingSource.Ground:
      return groundHeatSourceTemperature ?? null

    case HeatingOrCoolingSource.Air:
      if (airHeatSourceMinTemperature === undefined) {
        return null
      }

      return outdoorTemperature >= airHeatSourceMinTemperature
        ? outdoorTemperature
        : null

    case HeatingOrCoolingSource.EnergyBus:
      // Business decision during hybrid heat pump development (verified later in April 2023 from Joni Hilpinen): This temperature should come from the energy bus, using a hardcoded value until that is implemented.
      return 2

    default:
      return null
  }
}

export function sourceToHeatSourceType(
  source: HeatingOrCoolingSource,
): HeatSourceType {
  if (source === HeatingOrCoolingSource.None) {
    throw new Error('"None" heat source type does not exist')
  }

  const typeMap = {
    [HeatingOrCoolingSource.Ground]: HeatSourceType.Ground,
    [HeatingOrCoolingSource.Air]: HeatSourceType.Air,
    [HeatingOrCoolingSource.EnergyBus]: HeatSourceType.EnergyBus,
  }

  return typeMap[source]
}

function getHeatSourceMaxPower(
  source: HeatingOrCoolingSource,
  groundHeatSourceMaxPower: number | undefined,
  airHeatSourceMinTemperature: number | undefined,
  heatingDemand: number,
  outdoorTemperature: number,
): number | null {
  switch (source) {
    case HeatingOrCoolingSource.Ground:
      return groundHeatSourceMaxPower ?? Infinity

    case HeatingOrCoolingSource.Air:
      if (airHeatSourceMinTemperature === undefined) {
        return null
      }

      return outdoorTemperature >= airHeatSourceMinTemperature
        ? heatingDemand
        : null

    case HeatingOrCoolingSource.EnergyBus:
      return heatingDemand

    default:
      return null
  }
}

export function createHeatSourceFromHeatingSource(
  heatSource: HeatingOrCoolingSource | undefined,
  groundHeatSourceTemperature: number | undefined,
  groundHeatSourceMaxPower: number | undefined,
  airHeatSourceMinTemperature: number | undefined,
  heatingDemand: number,
  outdoorTemperature: number,
): HeatSource | null {
  if (!heatSource || heatSource === HeatingOrCoolingSource.None) {
    return null
  }

  const temperature = getHeatSourceTemperature(
    heatSource,
    groundHeatSourceTemperature,
    airHeatSourceMinTemperature,
    outdoorTemperature,
  )

  const maxPower = getHeatSourceMaxPower(
    heatSource,
    groundHeatSourceMaxPower,
    airHeatSourceMinTemperature,
    heatingDemand,
    outdoorTemperature,
  )

  if (temperature === null || maxPower === null) {
    return null
  }

  return {
    type: sourceToHeatSourceType(heatSource),
    temperature,
    maxPower,
  }
}

export function createHeatSourceFromWasteSource(
  wasteHeatSourcePower: number | undefined,
  wasteHeatSourceTemperature: number | undefined,
): HeatSource | null {
  if (
    wasteHeatSourcePower === undefined ||
    wasteHeatSourceTemperature === undefined
  ) {
    return null
  }

  return {
    type: HeatSourceType.Waste,
    temperature: wasteHeatSourceTemperature,
    maxPower: wasteHeatSourcePower,
  }
}

export function calculateHeatCollectionTemperature(
  heatSources: HeatSource[],
): number {
  const totalCollectedHeat = heatSources
    .map((heatSource) => heatSource.collectedHeat ?? 0)
    .reduce(reduceToSum, 0)

  // Usage-weighted average of heat source temperatures.
  return heatSources
    .map(
      (heatSource) =>
        ((heatSource.collectedHeat ?? 0) / totalCollectedHeat) *
        heatSource.temperature,
    )
    .reduce(reduceToSum, 0)
}
